import { Reveal } from "./Reveal";
import { useT } from "../../i18n/locale-context";
import { CheckIcon, HandIcon, InboxIcon, ListCheckIcon, ReplyIcon, ShieldIcon } from "../icons";

function Heading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">{eyebrow}</p>
      <h2 className="font-display mt-3 text-[1.75rem] font-semibold leading-[1.08] text-slate-900 sm:text-[2.4rem]">{title}</h2>
    </div>
  );
}

const DOES_ICONS = [InboxIcon, ReplyIcon, ListCheckIcon, HandIcon];

export function WhatItDoes() {
  const d = useT().landing.does;
  return (
    <section id="what-it-does" className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
      <Reveal>
        <Heading eyebrow={d.eyebrow} title={d.title} />
      </Reveal>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {d.items.map((item, i) => {
          const Icon = DOES_ICONS[i] ?? InboxIcon;
          return (
            <Reveal key={item.title} delay={i * 80}>
              <div className="glass-card glass-lift h-full p-6">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                  <Icon className="h-5 w-5" />
                </span>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{item.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

export function DayWithOctoMail() {
  const d = useT().landing.day;
  return (
    <section id="see-it-work" className="px-5 py-8 sm:px-8 sm:py-10">
      <div className="glass-panel mx-auto max-w-6xl px-5 py-14 sm:px-10 sm:py-16">
        <Reveal>
          <Heading eyebrow={d.eyebrow} title={d.title} />
        </Reveal>
        <ol className="mt-10 grid gap-4 md:grid-cols-3">
          {d.steps.map((s, i) => (
            <li key={s.time}>
              <Reveal delay={i * 110} className="h-full">
                <div className="glass-card flex h-full flex-col p-5">
                  <p className="font-display text-sm font-semibold text-brand-600">
                    {s.time} · {s.title}
                  </p>
                  {i === 0 && (
                    <p className="mt-3 max-w-[92%] rounded-2xl rounded-bl-md bg-slate-100 px-3.5 py-2.5 text-sm leading-relaxed text-slate-800">{s.message}</p>
                  )}
                  {i === 1 && (
                    <p className="mt-3 ml-auto max-w-[92%] rounded-2xl rounded-br-md bg-brand-600 px-3.5 py-2.5 text-sm leading-relaxed text-white">{s.message}</p>
                  )}
                  {i === 2 && (
                    <dl className="mt-3 space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm">
                      {d.lead.map((row) => (
                        <div key={row.k} className="flex justify-between gap-3">
                          <dt className="text-slate-600">{row.k}</dt>
                          <dd className="text-right font-semibold text-slate-900">{row.v}</dd>
                        </div>
                      ))}
                      <div className="flex justify-between gap-3">
                        <dt className="text-slate-600">{d.statusLabel}</dt>
                        <dd className="inline-flex items-center gap-1 font-semibold text-brand-700">
                          <CheckIcon className="h-3.5 w-3.5" />
                          {d.status}
                        </dd>
                      </div>
                    </dl>
                  )}
                  <p className="mt-auto pt-3 text-xs text-slate-600">{s.note}</p>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function YouStayInControl() {
  const c = useT().landing.control;
  return (
    <section id="control" className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20">
      <Reveal>
        <Heading eyebrow={c.eyebrow} title={c.title} />
      </Reveal>
      <ul className="mx-auto mt-10 grid max-w-4xl gap-3 sm:grid-cols-2">
        {c.items.map((item, i) => (
          <li key={item}>
            <Reveal delay={i * 70} className="h-full">
              <div className="glass-card glass-lift flex h-full items-start gap-3 p-5 text-[15px] leading-relaxed text-slate-700">
                <ShieldIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
                <span>{item}</span>
              </div>
            </Reveal>
          </li>
        ))}
      </ul>
    </section>
  );
}
